import { Bug, CircleAlert, CircleCheck, CircleDashed, CircleDot, Clock, Lightbulb, ListTodo, SquareCheck, Eye } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  ConversationCategory,
  ConversationStatus,
  TaskPriority,
  TaskStatus,
  TaskType,
} from '../../common/models/support.model';

/** Icons and i18n keys for the support vocabulary. Colour only where it carries meaning. */
export const TASK_TYPE_META: Record<TaskType, { icon: LucideIcon; labelKey: string; tone: string }> = {
  bug: { icon: Bug, labelKey: 'SUPPORT.TASK_TYPE.BUG', tone: 'text-destructive' },
  feature: { icon: Lightbulb, labelKey: 'SUPPORT.TASK_TYPE.FEATURE', tone: 'text-warning' },
  issue: { icon: CircleAlert, labelKey: 'SUPPORT.TASK_TYPE.ISSUE', tone: 'text-foreground' },
  task: { icon: SquareCheck, labelKey: 'SUPPORT.TASK_TYPE.TASK', tone: 'text-muted-foreground' },
};

export const TASK_STATUS_META: Record<TaskStatus, { icon: LucideIcon; labelKey: string; dot: string }> = {
  backlog: { icon: CircleDashed, labelKey: 'SUPPORT.TASK_STATUS.BACKLOG', dot: 'bg-muted-foreground/40' },
  todo: { icon: ListTodo, labelKey: 'SUPPORT.TASK_STATUS.TODO', dot: 'bg-foreground/50' },
  in_progress: { icon: CircleDot, labelKey: 'SUPPORT.TASK_STATUS.IN_PROGRESS', dot: 'bg-warning' },
  review: { icon: Eye, labelKey: 'SUPPORT.TASK_STATUS.REVIEW', dot: 'bg-foreground' },
  done: { icon: CircleCheck, labelKey: 'SUPPORT.TASK_STATUS.DONE', dot: 'bg-success' },
};

export const TASK_PRIORITY_META: Record<TaskPriority, { labelKey: string; bars: number; tone: string }> = {
  low: { labelKey: 'SUPPORT.PRIORITY.LOW', bars: 1, tone: 'text-muted-foreground' },
  medium: { labelKey: 'SUPPORT.PRIORITY.MEDIUM', bars: 2, tone: 'text-muted-foreground' },
  high: { labelKey: 'SUPPORT.PRIORITY.HIGH', bars: 3, tone: 'text-foreground' },
  urgent: { labelKey: 'SUPPORT.PRIORITY.URGENT', bars: 4, tone: 'text-destructive' },
};

export const CONVERSATION_STATUS_META: Record<ConversationStatus, { icon: LucideIcon; labelKey: string; vendorLabelKey: string; dot: string }> = {
  open: { icon: CircleDot, labelKey: 'SUPPORT.STATUS.OPEN', vendorLabelKey: 'SUPPORT.STATUS.VENDOR_OPEN', dot: 'bg-warning' },
  waiting: { icon: Clock, labelKey: 'SUPPORT.STATUS.WAITING', vendorLabelKey: 'SUPPORT.STATUS.VENDOR_WAITING', dot: 'bg-foreground' },
  resolved: { icon: CircleCheck, labelKey: 'SUPPORT.STATUS.RESOLVED', vendorLabelKey: 'SUPPORT.STATUS.VENDOR_RESOLVED', dot: 'bg-success' },
};

export const CATEGORY_META: Record<ConversationCategory, { labelKey: string; icon: LucideIcon }> = {
  question: { labelKey: 'SUPPORT.CATEGORY.QUESTION', icon: CircleAlert },
  bug: { labelKey: 'SUPPORT.CATEGORY.BUG', icon: Bug },
  feature: { labelKey: 'SUPPORT.CATEGORY.FEATURE', icon: Lightbulb },
  billing: { labelKey: 'SUPPORT.CATEGORY.BILLING', icon: SquareCheck },
  other: { labelKey: 'SUPPORT.CATEGORY.OTHER', icon: CircleDashed },
};

// ─── Attachment rules (mirror the API: support.constants.ts) ─────────────

export const MAX_ATTACHMENTS = 6;
const MB = 1024 * 1024;
export const ATTACHMENT_RULES: Record<string, { kind: 'image' | 'video' | 'file'; maxBytes: number }> = {
  'image/jpeg': { kind: 'image', maxBytes: 10 * MB },
  'image/png': { kind: 'image', maxBytes: 10 * MB },
  'image/gif': { kind: 'image', maxBytes: 10 * MB },
  'image/webp': { kind: 'image', maxBytes: 10 * MB },
  'video/mp4': { kind: 'video', maxBytes: 50 * MB },
  'video/quicktime': { kind: 'video', maxBytes: 50 * MB },
  'video/webm': { kind: 'video', maxBytes: 50 * MB },
  'application/pdf': { kind: 'file', maxBytes: 10 * MB },
};
export const ACCEPT_ATTR = Object.keys(ATTACHMENT_RULES).join(',');

/** Why a file can't be attached, or null. Checked before uploading so a 200 MB video fails fast. */
export function attachmentProblem(file: File): { key: string; opts?: Record<string, unknown> } | null {
  const rule = ATTACHMENT_RULES[file.type];
  if (!rule) return { key: 'SUPPORT.COMPOSER.TYPE_NOT_ALLOWED', opts: { name: file.name } };
  if (file.size > rule.maxBytes) return { key: 'SUPPORT.COMPOSER.TOO_LARGE', opts: { name: file.name, max: Math.round(rule.maxBytes / MB) } };
  return null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

/** Where the vendor is, sent with a new conversation. */
export function captureContext(pageTitle?: string) {
  return {
    page: pageTitle || window.location.pathname,
    url: window.location.href.slice(0, 1000),
    appVersion: process.env.REACT_APP_VERSION,
    userAgent: navigator.userAgent.slice(0, 400),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    locale: (document.documentElement.lang || navigator.language || '').slice(0, 10),
  };
}
